import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import Footer from '../components/Footer'

const faqs = [
  {
    q: 'What is Solarch?',
    a: 'Solarch is an open-source Backend-as-a-Service (BaaS) written in TypeScript. It provides SQLite, Express, WebSocket, authentication, file storage, and an admin UI — all in a single npm package. Read our <a href="/docs/overview/introduction" class="text-primary underline hover:text-brand-cyan transition-colors">architecture introduction</a> to learn how everything fits into one process.',
  },
  {
    q: 'How is this different from PocketBase?',
    a: 'While inspired by PocketBase, Solarch is built entirely in TypeScript with a Node.js runtime. This means you get native npm ecosystem access, TypeScript-first DX, easy customization through code, and no Go dependency. It also includes MFA/TOTP and S3-compatible storage. Follow our <a href="/docs/overview/getting-started" class="text-primary underline hover:text-brand-cyan transition-colors">Quickstart Guide</a> to explore the developer experience, or read our in-depth <a href="/comparisons/pocketbase" class="text-primary underline hover:text-brand-cyan transition-colors">Solarch vs. PocketBase comparison guide</a>.',
  },
  {
    q: 'Do I need Docker or a database?',
    a: 'No. Solarch uses SQLite as its embedded database, so there\'s zero infrastructure to manage. Just run <code class="rounded bg-theme-surface px-1.5 py-0.5 text-sm text-brand-cyan font-mono">npx solarch serve</code> and you\'re running. Learn more about Write-Ahead Logging and concurrency in our <a href="/docs/data-services/database" class="text-primary underline hover:text-brand-cyan transition-colors">SQLite database guide</a>. For production, you can optionally connect S3 for file storage.',
  },
  {
    q: 'Can I use Solarch in production?',
    a: 'Absolutely. Solarch is designed for production use with built-in security features including Helmet, rate limiting, CORS configuration, JWT-based auth, SQL injection protection, and MFA/TOTP support. See our <a href="/docs/security-auth/security" class="text-primary underline hover:text-brand-cyan transition-colors">Security Rules Guide</a> and <a href="/docs/security-auth/authentication" class="text-primary underline hover:text-brand-cyan transition-colors">Authentication Reference</a> for configuration details.',
  },
  {
    q: 'How do I deploy Solarch?',
    a: 'Deploy it anywhere Node.js runs — VPS, Railway, Fly.io, Render, or your own server. Since it\'s a single process with SQLite, deployment is straightforward. Follow our step-by-step <a href="/docs/hosting-deployment/deployment" class="text-primary underline hover:text-brand-cyan transition-colors">Production Deployment Guide</a>, and configure scalable media using <a href="/docs/file-storage/s3-configuration" class="text-primary underline hover:text-brand-cyan transition-colors">S3 Storage</a>.',
  },
  {
    q: 'Is there a hosted/cloud version?',
    a: 'Not yet. Solarch is self-hosted and open source (Apache-2.0). You own your data completely. A cloud-hosted option is on the roadmap.',
  },
  {
    q: 'How can I contribute?',
    a: 'Check out the <a href="https://github.com/xvertere-org/Solarch/blob/main/CONTRIBUTING.md" target="_blank" rel="noreferrer" class="text-primary underline transition-colors hover:text-brand-cyan">contributing guide</a> on GitHub. We welcome issues, pull requests, and discussions.',
  },
]

function FaqItem({ faq, index }: { faq: typeof faqs[0]; index: number }) {
  const [open, setOpen] = useState(false)
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className=""
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-5 text-left text-base font-medium text-theme transition-colors hover:text-primary"
      >
        <span>{faq.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-theme-muted transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p
          className="text-sm text-theme-tertiary leading-relaxed"
          dangerouslySetInnerHTML={{ __html: faq.a }}
        />
      </div>
    </motion.div>
  )
}

export default function FAQ() {
  return (
    <>
      <SEO 
        title="Solarch FAQ — Architecture, PocketBase Comparison & Deployment"
        description="Frequently asked questions about Solarch: TypeScript-native architecture, PocketBase comparison, embedded SQLite requirements, production readiness, and self-hosted deployment."
        canonical="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "about": {
            "@id": "https://solarch.in/#software"
          },
          "publisher": {
            "@id": "https://solarch.in/#organization"
          },
          "mainEntity": faqs.map((faq) => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.a.replace(/<[^>]*>/g, ''),
            },
          })),
        }}
      />

      <div className="min-h-screen bg-theme-body pt-14">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary blur-3xl" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 pt-16 pb-6 sm:px-6 sm:pt-20 sm:pb-8 lg:px-8 lg:pt-24 lg:pb-10">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h1 className="font-heading text-4xl font-bold tracking-tight text-theme sm:text-5xl">
                Frequently Asked Questions
              </h1>
              <p className="mx-auto mt-3 max-w-xl text-lg text-theme-tertiary">
                Everything you need to know about Solarch.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 pt-4 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          {faqs.map((faq, i) => (
            <FaqItem key={i} faq={faq} index={i} />
          ))}
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </>
  )
}
